import React from 'react';
import { Check } from 'lucide-react';
import {
    resolveMarketDisplayAmountMeta,
    resolveNormalizationExplainabilityMeta
} from '../shared/shoppingDisplayText';

/**
 * ShoppingItemCard
 * Regular modes use a compact list card, FINAL mode switches to a larger review form.
 *
 * @param {object} item
 * @param {'VIEW' | 'EDIT' | 'CHECK' | 'FINAL'} mode
 */
const ShoppingItemCard = ({
    item,
    onQuantityChange,
    onUnitChange,
    onBrandChange,
    onAlternativeProductChange,
    onPriceChange,
    onPlacementChange,
    onDelete,
    onToggleCheck,
    mode = 'EDIT',
    viewMode = 'USER'
}) => {
    const isChecked = item.checked || item.isChecked;
    const isAlacard = item.source === 'ALACART' || item.note?.includes('Alakart');
    const isInInventory = item.isInInventory || item.matchPercentage > 0;
    const displayAmount = resolveMarketDisplayAmountMeta({
        ...item,
        unit: item?.unit || 'AD'
    }).display;
    const normalizationMeta = resolveNormalizationExplainabilityMeta(item);
    const lastKnownPromptValue = Number(normalizationMeta?.snapshot?.userAnswer || 0);
    const lastKnownPromptUnit = String(normalizationMeta?.snapshot?.marketUnit || '').trim();
    const estimatedPrice = Number(item.priceAtTime || 0);
    const finalAmount = item.finalAmount ?? item.suggestedAmount ?? '';
    const displayPrice = Number(item.price || item.priceAtTime || 0);
    const paidPrice = item.price || item.priceAtTime || '';
    // [Faz 1.3 DIA] Düşük-güven fiyat bayrağı (conf<0.3). Alan yoksa eski davranış (rozet yok).
    const priceEstimated = item.priceEstimated === true
        || (Number(item.priceConfidence ?? 1) > 0 && Number(item.priceConfidence) < 0.3);
    // [Faz 4.5] Bölgesel median (gerçek kullanıcı raporları) → "doğrulanmış fiyat" rozeti (düşük-güvenin önünde).
    const priceVerified = item.priceVerified === true;
    const priceLabel = item.priceLabel || '';

    const normalizeUnit = (value = '') => String(value || '').trim().toLocaleLowerCase('tr-TR');
    const currentUnit = normalizeUnit(item.unit || 'adet') || 'adet';
    const canonicalUnit = normalizeUnit(
        item.canonicalUnit ||
        item.normalizedUnit ||
        item.canonicalSourceSnapshot?.resolvedCanonicalUnit ||
        item.canonicalSourceSnapshot?.userAnswerUnit ||
        currentUnit
    );
    const unitOptions = (() => {
        const options = new Set([currentUnit]);
        if (['g', 'gr', 'gram', 'kg'].includes(canonicalUnit)) {
            options.add('gr');
            options.add('kg');
        } else if (['ml', 'lt', 'l', 'litre'].includes(canonicalUnit)) {
            options.add('ml');
            options.add('lt');
        } else {
            options.add('adet');
        }
        return [...options];
    })();

    void onBrandChange;
    void onAlternativeProductChange;
    void onPlacementChange;
    void viewMode;

    const getCategoryIcon = (cat) => {
        const c = (cat || 'OTHER').toUpperCase();
        if (c.includes('ET') || c.includes('MEAT')) return '🥩';
        if (c.includes('SEBZE') || c.includes('VEG')) return '🥦';
        if (c.includes('MEYVE') || c.includes('FRUIT')) return '🍎';
        if (c.includes('SÜT') || c.includes('DAIRY')) return '🥛';
        if (c.includes('BAKL') || c.includes('LEGUME')) return '🫘';
        if (c.includes('BAHARAT') || c.includes('SPICE')) return '🧂';
        return '🛒';
    };

    if (mode === 'FINAL') {
        return (
            <div
                className={`group relative rounded-[1.5rem] border p-3 sm:rounded-[1.8rem] sm:p-4 transition-all duration-300 ${
                    isChecked
                        ? 'border-blue-500/20 bg-blue-500/5 opacity-60'
                        : 'border-slate-200 dark:border-white/5 bg-slate-100 dark:bg-[#161c28] shadow-xl hover:border-slate-200 dark:border-white/10'
                }`}
            >
                <div className="flex items-start gap-2.5 sm:gap-3">
                    <div className="flex size-11 sm:size-14 shrink-0 items-center justify-center rounded-2xl border border-slate-200 dark:border-white/5 bg-white dark:bg-[#0a0e17] text-[22px] sm:text-[26px] shadow-inner">
                        {getCategoryIcon(item.category)}
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="mb-1 flex items-center gap-2">
                                    <h3
                                        className={`truncate text-[18px] sm:text-[22px] font-black leading-none tracking-tight ${
                                            isChecked ? 'text-slate-500 dark:text-gray-500 line-through' : 'text-slate-900 dark:text-gray-100'
                                        }`}
                                    >
                                        {item.ingredientName || item.name}
                                    </h3>
                                    {isInInventory && (
                                        <span className="shrink-0 rounded-full border border-green-500/20 bg-green-500/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-green-400">
                                            Evde Var
                                        </span>
                                    )}
                                </div>

                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <span className="text-base font-black uppercase text-blue-400">
                                        {displayAmount}
                                    </span>
                                    <span
                                        className={`text-[10px] font-black uppercase tracking-[0.18em] ${
                                            isAlacard ? 'text-orange-400' : 'text-slate-500 dark:text-gray-500'
                                        }`}
                                    >
                                        • {isAlacard ? 'ALAKART' : 'MANUEL'}
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-start gap-2">
                                {onDelete && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(item.id);
                                        }}
                                        className="rounded-xl p-2 text-slate-400 dark:text-gray-600 transition-colors hover:text-red-400"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">delete</span>
                                    </button>
                                )}

                                {paidPrice > 0 && (
                                    <div className="rounded-2xl border border-green-500/10 bg-green-500/5 px-3 py-2">
                                        <span className="text-base font-black text-green-400">
                                            {paidPrice} ₺
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-3 sm:mt-4 rounded-[1.25rem] sm:rounded-[1.5rem] border border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-[#0c1220] p-3 sm:p-4">
                    <div className="flex flex-col gap-2.5 sm:gap-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 flex-1">
                                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
                                    Alınan Marka
                                </div>
                                <div className="mt-2 inline-flex max-w-full rounded-full border border-slate-200 dark:border-white/10 bg-slate-100 dark:bg-[#111827] px-4 py-2 text-sm font-bold text-slate-900 dark:text-gray-100">
                                    <span className="truncate">
                                        {item.brand?.trim() || 'Marka girilmedi'}
                                    </span>
                                </div>
                            </div>

                            {estimatedPrice > 0 && (
                                <div className={`rounded-2xl border px-4 py-3 sm:min-w-[132px] ${
                                    priceVerified
                                        ? 'border-emerald-500/30 bg-emerald-500/10'
                                        : priceEstimated
                                            ? 'border-amber-500/30 bg-amber-500/10'
                                            : 'border-sky-500/15 bg-sky-500/10'
                                }`}>
                                    <div className={`text-[9px] font-black uppercase tracking-[0.18em] ${
                                        priceVerified ? 'text-emerald-500' : priceEstimated ? 'text-amber-500' : 'text-slate-500'
                                    }`}>
                                        {priceVerified ? '✓ Doğrulanmış' : priceEstimated ? 'Düşük Güven' : 'Tahmini'}
                                    </div>
                                    <div className={`mt-1 text-lg font-black ${
                                        priceVerified ? 'text-emerald-400' : priceEstimated ? 'text-amber-400' : 'text-sky-300'
                                    }`}>
                                        {estimatedPrice.toLocaleString('tr-TR')} ₺
                                    </div>
                                    {priceVerified ? (
                                        <div className="mt-0.5 text-[8px] font-bold text-emerald-500/80">{priceLabel || 'kullanıcılarca doğrulandı'}</div>
                                    ) : priceEstimated ? (
                                        <div className="mt-0.5 text-[8px] font-bold text-amber-500/80">fiyat doğrulanmadı</div>
                                    ) : null}
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-[minmax(0,1fr)_82px_minmax(0,1.15fr)] gap-2 sm:grid-cols-[minmax(0,1fr)_96px_minmax(0,1fr)] sm:gap-3">
                            <label className="flex flex-col gap-1.5">
                                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500 dark:text-gray-500">
                                    Miktar
                                </span>
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    step="any"
                                    min="0"
                                    value={finalAmount}
                                    placeholder="0"
                                    onFocus={(e) => e.currentTarget.select()}
                                    onChange={(e) => onQuantityChange?.(item.id, e.target.value)}
                                    className="h-12 sm:h-14 w-full min-w-0 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0a0e17] px-3 text-lg sm:text-xl font-black text-amber-400 outline-none transition-all placeholder:text-gray-700 focus:border-amber-500/50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                />
                            </label>

                            <label className="flex flex-col gap-1.5">
                                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500 dark:text-gray-500">
                                    Birim
                                </span>
                                <select
                                    value={currentUnit}
                                    onChange={(e) => onUnitChange?.(item.id, e.target.value)}
                                    className="h-12 sm:h-14 w-full min-w-0 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0a0e17] px-2 text-xs font-black uppercase text-slate-700 dark:text-slate-200 outline-none transition-all focus:border-blue-500/50"
                                >
                                    {unitOptions.map((unit) => (
                                        <option key={unit} value={unit}>{unit}</option>
                                    ))}
                                </select>
                            </label>

                            <label className="flex flex-col gap-1.5">
                                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500 dark:text-gray-500">
                                    Ödenen
                                </span>
                                <div className="relative">
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        step="0.01"
                                        min="0"
                                        value={paidPrice}
                                        placeholder="0,00"
                                        onFocus={(e) => e.currentTarget.select()}
                                        onChange={(e) => onPriceChange?.(item.id, e.target.value)}
                                        className="h-12 sm:h-14 w-full min-w-0 rounded-xl sm:rounded-2xl border border-[#22c55e]/20 bg-[#22c55e]/5 pl-3 pr-8 sm:pr-10 text-lg sm:text-xl font-black text-[#22c55e] outline-none transition-all placeholder:text-gray-700 focus:border-[#22c55e]/50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    />
                                    <span className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 text-base font-black text-[#22c55e]">
                                        ₺
                                    </span>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`group relative flex items-center gap-4 rounded-[1.6rem] p-4 transition-all duration-300 ${
                isChecked
                    ? 'border border-blue-500/20 bg-blue-500/5 opacity-60'
                    : 'border border-slate-200 dark:border-white/5 bg-slate-100 dark:bg-[#161c28] shadow-xl hover:border-slate-200 dark:border-white/10'
            }`}
        >
            <div
                className={`flex size-12 shrink-0 items-center justify-center rounded-2xl border border-slate-200 dark:border-white/5 text-2xl shadow-inner ${
                    isChecked ? 'bg-gray-800' : 'bg-white dark:bg-[#0a0e17]'
                }`}
            >
                {getCategoryIcon(item.category)}
            </div>

            <div className="min-w-0 flex-1">
                <div className="mb-0.5 flex items-center gap-2">
                    <h3
                        className={`truncate text-base font-black tracking-tight ${
                            isChecked ? 'text-slate-500 dark:text-gray-500 line-through' : 'text-slate-900 dark:text-gray-100'
                        }`}
                    >
                        {item.ingredientName || item.name}
                    </h3>
                    {isInInventory && (
                        <span className="shrink-0 rounded-full border border-green-500/20 bg-green-500/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-green-400">
                            Evde Var
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-xs font-bold uppercase text-blue-400">
                        {displayAmount}
                    </span>

                    <span
                        className={`text-[9px] font-black uppercase tracking-widest ${
                            isAlacard ? 'text-orange-400' : 'text-slate-400 dark:text-gray-600'
                        }`}
                    >
                        • {isAlacard ? 'ALAKART' : 'MANUEL'}
                    </span>

                    {item.brand && (
                        <span className="max-w-[120px] truncate text-[10px] font-bold text-primary">
                            • {item.brand}
                        </span>
                    )}
                </div>

                {normalizationMeta.showCanonicalDetail && (
                    <div className="mt-2 flex flex-col gap-1">
                        <span className="text-[10px] font-bold text-amber-300">
                            İç hesap: ≈ {normalizationMeta.canonicalDisplay}
                        </span>
                        {lastKnownPromptValue > 0 && lastKnownPromptUnit && (
                            <span className="text-[10px] font-medium text-slate-400">
                                Geçen sefer: 1 {lastKnownPromptUnit} = {lastKnownPromptValue}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="flex shrink-0 items-center gap-2">
                {onDelete && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(item.id);
                        }}
                        className={`rounded-xl p-2 transition-colors ${
                            isChecked ? 'text-gray-800' : 'text-slate-400 dark:text-gray-600 hover:text-red-400'
                        }`}
                    >
                        <span className="material-symbols-outlined text-[20px]">delete</span>
                    </button>
                )}

                {mode === 'CHECK' && onToggleCheck && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleCheck(item.id);
                        }}
                        className={`flex h-11 min-w-[96px] items-center justify-center gap-2 rounded-2xl px-4 text-[11px] font-black uppercase tracking-[0.14em] transition-all ${
                            isChecked
                                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                                : 'border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0a0e17] text-slate-300 hover:border-blue-500/30 hover:text-white'
                        }`}
                        aria-label={isChecked ? 'Ürün seçildi' : 'Ürünü seç'}
                    >
                        <Check size={18} strokeWidth={3} />
                        <span>Aldım</span>
                    </button>
                )}

                {(mode === 'VIEW' || isChecked) && displayPrice > 0 && (
                    <div className="rounded-xl border border-green-500/10 bg-green-500/5 px-3 py-1.5">
                        <span className="text-xs font-black text-green-400">{displayPrice.toLocaleString('tr-TR')} ₺</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ShoppingItemCard;
